import React, { useState } from 'react'
import { ChevronDown, MessageCircle } from 'lucide-react'

const faqs = [
  {
    question: 'What plan do you offer?',
    answer: 'LaundryMate offers one simple plan. You get full access to order management, CRM, inventory, staff tracking, analytics, loyalty tools, and mobile access. SMS credits are optional, and you can cancel anytime.'
  },
  {
    question: 'Can I use LaundryMate on my mobile device?',
    answer: 'Yes! LaundryMate is a Progressive Web App (PWA) that works seamlessly on all devices. You can install it on your phone or tablet and use it just like a native app.'
  },
  {
    question: 'Is my data secure?',
    answer: 'Absolutely. We use bank-level encryption (SSL/TLS) to protect your data. All information is stored securely in the cloud with regular backups. We never share your data with third parties.'
  },
  {
    question: 'Can I manage multiple shop locations?',
    answer: 'Yes. If you manage multiple shop locations, we can set up a custom multi-branch arrangement for you. Message us so we can recommend the best setup for your stores.'
  },
  {
    question: 'What kind of support do you offer?',
    answer: 'We offer chat and email support for all customers, plus onboarding help during setup. If you need a larger rollout for multiple branches, we can also arrange dedicated training and support.'
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees. If you cancel, you\'ll have access until the end of your billing period.'
  },
  {
    question: 'Do you offer training for my staff?',
    answer: 'Yes! We provide comprehensive documentation and video tutorials for all users. Enterprise customers get personalized training sessions and onboarding support.'
  },
  {
    question: 'Can I import my existing customer data?',
    answer: 'Yes, we can help you import your existing customer data from spreadsheets or other systems. Contact our support team for assistance with data migration.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept major credit cards, debit cards, and online payment methods. For Enterprise plans, we also offer bank transfer and custom billing arrangements.'
  },
  {
    question: 'Is there a setup fee?',
    answer: 'No setup fees! You only pay the monthly subscription fee. We want to make it as easy as possible for you to get started.'
  }
]

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null)

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section id="faq" className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked
            <span className="gradient-text"> Questions</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">
            Everything you need to know about LaundryMate
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden transition-all duration-300 hover:border-cyan-500"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <span className="font-semibold text-gray-900 dark:text-white pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 text-gray-600 dark:text-gray-300 leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 text-center bg-gradient-to-r from-cyan-50 dark:from-gray-700 to-blue-50 dark:to-gray-700 rounded-2xl p-8 transition-colors duration-300">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Still have questions?
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://m.me/946243658562372"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              <MessageCircle className="w-5 h-5" />
              Message us on Messenger
            </a>
            <a
              href="mailto:orders@laundrymate.shop"
              className="inline-flex items-center gap-2 bg-white dark:bg-gray-600 text-gray-700 dark:text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 border-2 border-cyan-200 dark:border-gray-500 hover:border-cyan-400 dark:hover:border-cyan-400 hover:scale-105 active:scale-95"
            >
              Email Support
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
